import { DateTimeValue, spinner, Table, TableColumnType } from '@/components';
import { useAppActivityList } from '@/core';
import { Activity } from '@webinex/activity';
import { FilterRule, NotificationFieldValue } from '@webinex/wispo';
import { Divider } from 'antd';
import { TablePaginationConfig } from 'antd/lib/table/interface';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { ActivityAction } from '../Activity/ActivityAction';
import { ActivityKind } from '../Activity/ActivityKind';
import { ActivityTableSearchPanel } from './Search/ActivityTableSearchPanel';
import { useSorting } from '@/components/Collections.Paging/useSorting';
import style from './ActivityTablePanel.module.scss';
import moment from 'moment';
import { DATE_TIME_UTC_OFFSET_HOURS } from '@/platform';

const DEFAULT_PAGING: TablePaginationConfig = {
  pageSize: 20,
  current: 1,
  pageSizeOptions: ['10', '20', '50'],
};

export const DEFAULT_FILTERS: FilterRule = {
  fieldId: 'success',
  operator: '=',
  value: true,
};

export const INITIAL_FILTERS: FilterRule = {
  operator: 'and',
  children: [
    {
      fieldId: 'success',
      operator: '=',
      value: true,
    },
    {
      fieldId: 'performedAt',
      operator: '>=',
      value: moment()
        .subtract(1, 'months')
        .startOf('day')
        .add(-DATE_TIME_UTC_OFFSET_HOURS, 'hours')
        .toISOString(true),
    },
  ],
};

export const DEFAULT_SORT = {
  fieldId: 'performedAt',
  dir: 'desc',
};

interface Props {
  className?: string;
}

function useColumns() {
  const { t } = useTranslation();

  return useMemo<TableColumnType<Activity>[]>(() => {
    return [
      {
        title: t('activity.table.key'),
        key: 'kind',
        dataIndex: 'kind',
        render: (_, { kind, id }) => (
          <NavLink to={`/activity/${id}`}>
            <ActivityKind>{kind}</ActivityKind>
          </NavLink>
        ),
      },
      {
        title: t('activity.table.actions'),
        key: 'audit-actions',
        dataIndex: 'audit-actions',
        render: (_, activity) => <ActivityAction activity={activity} />,
      },
      {
        title: t('activity.table.community'),
        key: 'community',
        dataIndex: 'communityNumber',
        render: (_, { communityNumber, communityName }) => (
          <NavLink target="_blank" to={`/item/${communityNumber}`}>
            {communityName}
          </NavLink>
        ),
      },
      {
        title: t('activity.table.home'),
        key: 'home',
        dataIndex: 'homeNumber',
        render: (_, { homeNumber, homeName }) => (
          <NavLink target="_blank" to={`/item/${homeNumber}`}>
            {homeName}
          </NavLink>
        ),
      },
      {
        title: t('activity.table.project'),
        key: 'project',
        dataIndex: 'projectNumber',
        render: (_, { projectNumber, projectName }) => (
          <NavLink target="_blank" to={`/item/${projectNumber}`}>
            {projectName}
          </NavLink>
        ),
      },
      {
        title: t('activity.table.performedAt'),
        key: 'performedAt',
        dataIndex: 'performedAt',
        sorter: (a, b) => a.performedAt.localeCompare(b.performedAt),
        render: (_, { performedAt }) => <DateTimeValue value={performedAt} />,
      },
      {
        title: t('activity.table.user'),
        key: 'userName',
        dataIndex: 'userName',
        render: (_, { userName }) => userName,
      },
    ];
  }, [t]);
}

function useViewState() {
  const { state, fetch } = useAppActivityList();
  const [paging, setPaging] = useState<TablePaginationConfig>(DEFAULT_PAGING);
  const [filters, setFilters] = useState<FilterRule>(INITIAL_FILTERS);
  const [sort, setSort] = useState<any>(DEFAULT_SORT);
  const { sorting, onSortingChange } = useSorting<Activity>();

  useEffect(() => {
    if (sorting?.sortBy && sorting.sortOrder) {
      setSort({
        fieldId: sorting.sortBy as NotificationFieldValue,
        dir: sorting.sortOrder === 'descend' ? 'desc' : 'asc',
      });
    }
  }, [sorting?.sortBy, sorting?.sortOrder]);

  useEffect(() => {
    spinner.show();

    const skip = (paging.current! - 1) * paging.pageSize!;
    const take = paging.pageSize!;

    fetch({
      paging: { skip, take },
      filter: filters,
      includeTotal: true,
      sort,
    }).finally(spinner.hide);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sort, paging]);

  return {
    items: Object.values(state.itemById),
    onTableChange: setPaging,
    paging: { ...paging, total: state.total },
    onFilterChange: setFilters,
    onSortingChange,
  };
}

export function ActivityTablePanel(props: Props) {
  const { className } = props;
  const { items, onTableChange, paging, onFilterChange, onSortingChange } =
    useViewState();
  const columns = useColumns();

  return (
    <div>
      <ActivityTableSearchPanel className="pb-3" onSubmit={onFilterChange} />
      <Divider className={className} />
      <Table<Activity>
        className={style.table}
        name="Activities"
        columns={columns}
        rowKey={(row) => row.id}
        dataSource={items}
        onChange={(value, _, sort) => {
          onTableChange(value);
          onSortingChange(sort);
        }}
        pagination={paging}
      />
    </div>
  );
}
